import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV, DOCTOR } from "@/data/content";
import { useBooking } from "@/context/BookingContext";

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
    <div className="relative w-9 h-9 rounded-xl bg-brand-light border border-brand/20 flex items-center justify-center">
      <span className="font-heading font-extrabold text-brand text-lg leading-none">A</span>
    </div>
    <div className="leading-tight">
      <div className="font-heading font-extrabold text-[#0b2c4d] text-[15px] tracking-tight">Dr. Arun Reddy M</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-brand">Endoscopic Spine Surgery</div>
    </div>
  </Link>
);

const DesktopNav = () => (
  <nav className="hidden lg:flex items-center gap-1">
    {NAV.map((item) => {
      const cls = "px-3 py-2 text-sm text-slate-600 hover:text-brand transition-colors relative group";
      const inner = (
        <>
          {item.label}
          <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-brand scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
        </>
      );
      const tid = `nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
      return item.href.includes("#") ? (
        <a key={item.href} href={item.href} data-testid={tid} className={cls}>{inner}</a>
      ) : (
        <Link key={item.href} to={item.href} data-testid={tid} className={cls}>{inner}</Link>
      );
    })}
  </nav>
);

const MENU_INITIAL = { height: 0, opacity: 0 };
const MENU_ANIMATE = { height: "auto", opacity: 1 };
const MENU_TRANSITION = { duration: 0.35, ease: [0.16, 1, 0.3, 1] };

const MobileMenu = ({ onClose, openBooking }) => (
  <motion.div
    initial={MENU_INITIAL}
    animate={MENU_ANIMATE}
    exit={MENU_INITIAL}
    transition={MENU_TRANSITION}
    className="lg:hidden overflow-hidden bg-white border-b border-slate-200"
  >
    <div className="px-6 py-4 flex flex-col gap-1">
      {NAV.map((item) => {
        const cls = "py-2.5 text-slate-700 hover:text-brand transition-colors border-b border-slate-100";
        const tid = `mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
        return item.href.includes("#") ? (
          <a key={item.href} href={item.href} onClick={onClose} data-testid={tid} className={cls}>{item.label}</a>
        ) : (
          <Link key={item.href} to={item.href} onClick={onClose} data-testid={tid} className={cls}>{item.label}</Link>
        );
      })}
      <div className="flex gap-3 pt-4">
        <Button
          onClick={() => { onClose(); openBooking("appointment"); }}
          className="flex-1 bg-brand hover:bg-brand-hover text-white font-semibold rounded-full"
          data-testid="mobile-book-btn"
        >
          Book Appointment
        </Button>
        <a href={`tel:${DOCTOR.phoneRaw}`} aria-label="Call" data-testid="mobile-call-link"
          className="w-11 h-11 rounded-full border border-brand/30 text-brand flex items-center justify-center">
          <Phone className="w-5 h-5" />
        </a>
      </div>
    </div>
  </motion.div>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="sticky-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-[0_4px_20px_rgba(11,44,77,0.06)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Logo />
        <DesktopNav />

        <div className="flex items-center gap-3">
          <Button
            data-testid="header-book-btn"
            onClick={() => openBooking("appointment")}
            className="hidden sm:inline-flex bg-brand hover:bg-brand-hover text-white font-semibold rounded-full px-5 h-10"
          >
            Book Appointment
          </Button>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-full border border-slate-300 flex items-center justify-center text-[#0b2c4d]"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && <MobileMenu onClose={() => setMobileOpen(false)} openBooking={openBooking} />}
      </AnimatePresence>
    </header>
  );
};

export default Header;
